import { PatientNameFormat, PDFConfigPlaceholder } from "../types/enums";
import {
	ClientEntityType,
	GuarantorEntityType,
	PatientEntityType,
	JobEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateType } from "./document-template.types";
import { formatDate } from "../lib/utils/utils-functions";

export function getRtfWidth(width?: number) {
	return width ? `${width}mm` : "100%";
}

/**
 * Replaces placeholder tokens (e.g. #WAEHRUNG, #EORI, #WAEHRUNGSRECHNER:CHF:EUR) in a
 * text/RTF string with their resolved values. Tokens are replaced longest-first so that
 * an overlapping token (#WAEHRUNGSRECHNER:CHF:EUR) is never partially clobbered by a
 * shorter one (#WAEHRUNG).
 */
export function replacePdfPlaceholders(
	text: string,
	values: Record<string, string>
): string {
	const tokens = Object.keys(values).sort((a, b) => b.length - a.length);
	let result = text;
	for (const token of tokens) {
		result = result.replace(new RegExp(token, "g"), values[token] ?? "");
	}
	return result;
}

/**
 * The currency-converter placeholder is parameterised with an explicit base and goal
 * ISO currency code, e.g. #WAEHRUNGSRECHNER:CHF:EUR ("show the document total converted
 * from CHF to EUR"). It is resolved at export time against a frankfurter.dev rate.
 */
const CURRENCY_CONVERTER_REGEX = new RegExp(
	`${PDFConfigPlaceholder.CURRENCY_CONVERTER}:([A-Za-z]{3}):([A-Za-z]{3})`,
	"g"
);

export interface CurrencyConverterToken {
	token: string;
	base: string;
	goal: string;
}

/**
 * Finds every #WAEHRUNGSRECHNER:BASE:GOAL occurrence in a text, returning the full token
 * plus its parsed base/goal currency codes (so the rates can be pre-fetched before render).
 */
export function extractCurrencyConverterPairs(
	text: string
): CurrencyConverterToken[] {
	const result: CurrencyConverterToken[] = [];
	const regex = new RegExp(CURRENCY_CONVERTER_REGEX);
	let match: RegExpExecArray | null;
	while ((match = regex.exec(text)) !== null) {
		// Keep the original token for replacement; normalise codes to uppercase ISO.
		result.push({
			token: match[0],
			base: match[1].toUpperCase(),
			goal: match[2].toUpperCase(),
		});
	}
	return result;
}

export function getPatientName(
	patient: {
		firstName: string | null;
		lastName: string | null;
		title: string | null;
		birth_date: string | null;
		code: string | null;
	},
	patientNameFormat: PatientNameFormat
) {
	const patientLastName = patient?.lastName ? patient.lastName.trim() : "";
	const patientFirstName = patient?.firstName ? patient.firstName.trim() : "";
	const patientTitle = patient?.title ? patient.title.trim() : "";
	let returnValue = "";
	if (patientNameFormat === PatientNameFormat.LAST_NAME_COMMA_FIRST_NAME) {
		if (patientLastName && patientFirstName) {
			returnValue = `${patientLastName}, ${patientFirstName}`;
		} else if (patientLastName) {
			returnValue = patientLastName;
		} else if (patientFirstName) {
			returnValue = patientFirstName;
		}
	} else if (
		patientNameFormat ===
		PatientNameFormat.LAST_NAME_COMMA_FIRST_NAME_COMMA_TITLE
	) {
		if (patientLastName && patientFirstName && patientTitle) {
			returnValue = `${patientLastName}, ${patientFirstName}, ${patientTitle}`;
		} else if (patientLastName && patientFirstName) {
			returnValue = `${patientLastName}, ${patientFirstName}`;
		} else if (patientLastName && patientTitle) {
			returnValue = `${patientLastName}, ${patientTitle}`;
		} else if (patientFirstName && patientTitle) {
			returnValue = `${patientFirstName}, ${patientTitle}`;
		} else if (patientLastName) {
			returnValue = patientLastName;
		} else if (patientFirstName) {
			returnValue = patientFirstName;
		} else if (patientTitle) {
			returnValue = patientTitle;
		}
	} else if (
		patientNameFormat ===
		PatientNameFormat.CODE_TITLE_LAST_NAME_FIRST_NAME_BIRTHDATE
	) {
		const patientCode = patient?.code ? patient.code : "";
		const patientBirthdate = patient?.birth_date
			? ` (${formatDate(patient.birth_date)})`
			: "";
		if (
			!patientTitle &&
			!patientLastName &&
			!patientFirstName &&
			!patientBirthdate
		) {
			returnValue = patientCode;
		} else {
			returnValue = `${patientCode ? `${patientCode}: ` : ""}${[
				patientTitle,
				patientLastName,
				patientFirstName,
			]
				.filter(Boolean)
				.join(" ")}${patientBirthdate}`;
		}
	}
	return returnValue.trim();
}

export function patientHasAddress(patient: {
	street: string | null;
	city: string | null;
	postal_code: string | null;
	country: string | null;
}) {
	return (
		patient.street && patient.city && patient.postal_code && patient.country
	);
}

type RecipientType = ClientEntityType | GuarantorEntityType | PatientEntityType;

// Structural address shape shared by clients, guarantors, patients and the
// whitelisted recipient of get_payment_reminder_document.
export type AddressRecipient = Pick<
	PatientEntityType,
	| "title"
	| "first_name"
	| "last_name"
	| "street"
	| "postal_code"
	| "city"
	| "country"
	| "additional_information"
> &
	Partial<
		Pick<
			ClientEntityType,
			| "company_name"
			| "is_company"
			| "is_correspondence_address"
			| "c_title"
			| "c_first_name"
			| "c_last_name"
			| "c_company_name"
			| "c_is_company"
			| "c_street"
			| "c_postal_code"
			| "c_city"
			| "c_country"
			| "c_additional_information"
		>
	>;

/**
 * Determines the appropriate recipient for a PDF document based on document type,
 * job payment settings, and available entities.
 *
 * @param params Object containing all required entities and document type
 * @returns The selected recipient entity
 */
export const getRecipient = ({
	job,
	patient,
	guarantor,
	client,
	templateType,
}: {
	job: JobEntityType;
	patient?: PatientEntityType;
	guarantor?: GuarantorEntityType;
	client: ClientEntityType;
	templateType: PdfTemplateType;
}): RecipientType => {
	// Spezielle Fälle für bestimmte Dokumenttypen
	switch (templateType) {
		case PdfTemplateType.LIEFERSCHEIN:
			// Lieferschein geht immer an den Kunden
			return client;

		case PdfTemplateType.SAMMELRECHNUNG:
			// Sammelrechnung geht an den angegebenen Empfänger (client oder guarantor)
			return client;
	}

	// Standard-Logik für alle anderen Dokumente
	if (job.is_paid_by_patient && patient) {
		return patient;
	}

	if (job.guarantor_id && guarantor) {
		return guarantor;
	}

	return client;
};

/**
 * Converts a string to UTF-8 bytes represented as a binary string (one character per byte).
 * This is used for QR codes where we want UTF-8 encoding without BOM.
 *
 * We encode to UTF-8 bytes manually, then represent each byte as a character.
 * The QR code will contain UTF-8 encoded data without BOM.
 *
 * This function handles all Unicode characters including German umlauts (ä, ö, ü)
 * correctly by encoding them as UTF-8 bytes.
 *
 * TextEncoder().encode() produces UTF-8 without BOM, but we ensure it here explicitly.
 */
export const convertToUtf8 = (str: string): string => {
	const encoder = new TextEncoder();
	let bytes = encoder.encode(str);

	// Strip BOM if present (0xEF, 0xBB, 0xBF)
	if (
		bytes.length >= 3 &&
		bytes[0] === 0xef &&
		bytes[1] === 0xbb &&
		bytes[2] === 0xbf
	) {
		bytes = bytes.slice(3);
	}

	let result = "";
	for (let i = 0; i < bytes.length; i++) {
		result += String.fromCharCode(bytes[i]);
	}

	return result;
};
